/**
 * Content for the idle world (D240 to D246). Self-contained: it does not touch the old
 * content schema, which is retired from play and will be removed later.
 *
 * Time is in milliseconds except the barn's capacity, which is in hours. Coins are whole up to
 * 2^53 and plain doubles past it (D256).
 */
import { z } from 'zod';
/** An id: lowercase snake_case. */
export const Id = z.string().regex(/^[a-z][a-z0-9_]*$/, 'ids are lowercase snake_case');
import economy from '../content/idle_economy.json' with { type: 'json' };
import farmTheme from '../content/themes/farm/idle.json' with { type: 'json' };
import computeTheme from '../content/themes/compute/idle.json' with { type: 'json' };
import motorsportTheme from '../content/themes/motorsport/idle.json' with { type: 'json' };
import arenaTheme from '../content/themes/arena/idle.json' with { type: 'json' };
import { mergeTheme } from './theme-merge.mjs';

/**
 * A number of coins: whole below 2^53, a plain double above it, where whole numbers can no
 * longer all be told apart (D256).
 */
const Coins = z
  .number()
  .nonnegative()
  .finite()
  .refine((n) => n > Number.MAX_SAFE_INTEGER || Number.isInteger(n), 'coins are whole below 2^53');

/** A milestone: owning `owned` of a producer multiplies its speed or its value by `multiplier`. */
export const IdleMilestone = z.object({
  owned: z.number().int().positive(),
  kind: z.enum(['speed', 'value']),
  multiplier: z.number().gt(1),
});

/** D454: the groups a producer can belong to. */
export const IdleProducerGroup = z.enum(['crop', 'animal']);

export const IdleProducer = z.object({
  id: Id,
  name: z.string(),
  /**
   * Coins to unlock it. Unlocking gives the first one. 0 means the world starts with it. After a
   * festival it costs this times `prices.unlockPerFestival` for each festival held (D352).
   */
  unlockCost: Coins,
  /** Cost of the second one. Each one after costs `costGrowth` times the last. */
  baseCost: Coins.refine((n) => n > 0),
  costGrowth: z.number().min(1.1).max(1.15),
  /** Time for one bar to fill, before speed milestones. */
  cycleMs: z.number().int().positive(),
  /** Coins one owned unit pays per full bar, before value milestones. */
  payout: Coins.refine((n) => n > 0),
  /**
   * Coins to hire the hand that runs it without tapping, before hand-cost effects, when its next
   * `prices.handUnits` units cost less (D352).
   */
  handCost: Coins.refine((n) => n > 0),
  milestones: z.array(IdleMilestone),
  /** Can be unlocked only once this many festivals have been held in this generation (D251). */
  needsFestivals: z.number().int().nonnegative().optional(),
  /**
   * D301: the second piece of land it stands on, by `map.lands` id. Absent: the world itself. A
   * producer on a land is outside the world's line and can be unlocked once a trait opens its land.
   */
  land: Id.optional(),
  /**
   * D454: the kind of producer it is, for an effect on a whole group ("crops pay twice") and for
   * step 3's links. A theme's own data, like its names; a theme may leave every producer without.
   */
  group: IdleProducerGroup.optional(),
});

/**
 * D454 step 3: a pair of producers that lift each other when their plots sit side by side: a crop
 * next to the animals it feeds, bees next to a flowering crop. `to` earns the bonus; `goods` is
 * what the map draws travelling along the lane from `from` to `to`.
 */
export const IdleLink = z.object({
  from: Id,
  to: Id,
  kind: z.enum(['feeds', 'pollinates']),
  goods: z.enum(['bales', 'sacks', 'bees', 'crates']),
});

/** A one-off building that lifts one producer's value by turning it into a product (D244). */
export const IdleChain = z.object({
  id: Id,
  name: z.string(),
  /** The producer it lifts. It can be built once that producer is owned. */
  producer: Id,
  /** What the producer is sold as once it is built: flour, cheese. */
  product: z.string(),
  cost: Coins.refine((n) => n > 0),
  /** Multiplies the producer's value. */
  multiplier: z.number().gt(1),
  /** Can be built only once this festival perk is owned (D250). */
  needsPerk: Id.optional(),
  /** D343: can be built only once this many festivals are counted, as a producer's `needsFestivals`. */
  needsFestivals: z.number().int().nonnegative().optional(),
  /** D343: a second building on a crop, built only after this chain (the bakery after the mill). */
  needsChain: Id.optional(),
});

/**
 * A multiplier an upgrade or a goal gives. speed shortens bars, value raises payouts, tap
 * raises tapped payouts, barn lengthens the barn, hand and upgrade scale the cost of hands and of
 * Almanac upgrades (below 1 is cheaper).
 */
export const IdleEffectSpec = z.object({
  /** find scales what a lucky find pays (D253, D254). barn is kept for old content and no longer changes the barn (D253). */
  stat: z.enum(['speed', 'value', 'tap', 'barn', 'hand', 'upgrade', 'find']),
  /** One producer, or every producer when absent. */
  producer: Id.optional(),
  /** D454: every producer of this group instead (`producer` absent). None, in a theme without groups. */
  group: IdleProducerGroup.optional(),
  factor: z.number().positive(),
  /**
   * A synergy (D251): the effect grows with how many of another producer are owned. It gives
   * 1 + (factor - 1) x floor(owned / every), so "every 10 wheat makes vines pay 1% more" is
   * factor 1.01, per { producer: wheat, every: 10 }.
   */
  per: z.object({ producer: Id, every: z.number().int().positive() }).optional(),
});

/**
 * D454: the Almanac's four branches and the permanent tree's four, the same in every theme. A
 * theme names each in `branches`.
 */
export const IDLE_ALMANAC_BRANCHES = ['production', 'tapping', 'hands', 'festival'] as const;
export const IDLE_HERITAGE_BRANCHES = ['start', 'taps', 'growth', 'land'] as const;
export const IdleAlmanacBranch = z.enum(IDLE_ALMANAC_BRANCHES);
export const IdleHeritageBranch = z.enum(IDLE_HERITAGE_BRANCHES);

/**
 * An Almanac upgrade: a step of the year's tree (D454), bought once a year. Each branch is a
 * stack of tiers of three or six steps; any open step of an open tier can be bought, in any
 * order. A tier opens once `tree.opensShare` of the tier before it is bought. A step is open
 * once its tier is and what else it needs is in place.
 */
export const IdleUpgrade = z.object({
  id: Id,
  name: z.string(),
  /**
   * D457: the step's short form for its tile, about 26 characters and three lines at 12px
   * ("Wheat pays 1.5×"). The step sheet keeps the full sentence. The theme's own words.
   */
  short: z.string().min(1),
  cost: Coins.refine((n) => n > 0),
  effect: IdleEffectSpec,
  /** D454: the branch it sits on. */
  branch: IdleAlmanacBranch,
  /** D454: its tier in the branch, from 1. */
  tier: z.number().int().positive(),
  /** Shown once this producer is owned. */
  needsProducer: Id.optional(),
  /** Shown once this festival perk is owned: an extra Almanac page (D250). */
  needsPerk: Id.optional(),
  /** Shown only once this many festivals have been held in this generation (D251, D252). */
  needsFestivals: z.number().int().nonnegative().optional(),
  /** Shown only once the year has run this many hours: a late page for a long year (D264). */
  needsYearHours: z.number().positive().optional(),
  /**
   * A fork (D252, D454): two steps side by side in one tier. Buying one closes the other until
   * the world is passed on.
   */
  choice: Id.optional(),
  /** D334: shown only once this many of a producer are owned this year: a milestone page. */
  needsOwned: z.object({ producer: Id, count: z.number().int().positive() }).optional(),
  /**
   * D334: the cost rises by this factor for each festival held this generation past
   * `needsFestivals` (or past none), so a page stays worth reading as the world grows.
   */
  costPerFestival: z.number().gt(1).optional(),
});

/**
 * A goal: met once in a year, when the player is there to see it. D334: its name may hold `{n}`,
 * the target of the tier on show. A long goal (in `longGoals`) is met once a generation instead.
 */
export const IdleGoal = z.object({
  id: Id,
  name: z.string(),
  /**
   * own: `count` of `producer`; earn: `coins` this year; chain: `chain` built; hands: `count`
   * hired. Long goals only (D334): festivals: `count` held this generation; perks: `count` perk
   * levels owned; lifetime: `coins` earned this generation.
   */
  kind: z.enum(['own', 'earn', 'chain', 'hands', 'festivals', 'perks', 'lifetime']),
  producer: Id.optional(),
  chain: Id.optional(),
  count: z.number().int().positive().optional(),
  coins: z.number().positive().optional(),
  /** D334: a long goal's tiers, each a target, rising. Absent: `count` or `coins` alone. */
  targets: z.array(z.number().positive()).optional(),
  reward: IdleEffectSpec,
  /** Heritage it pays when met, to spend and earned, as well as its bonus (D252). */
  heritage: z.number().int().positive().optional(),
});

/**
 * A festival perk, bought with Heritage to spend and kept for good (D244, D246). Each level costs
 * `cost` times `costGrowth` to the power of the levels already owned, and gives the effect again.
 */
export const IdlePerk = z.object({
  id: Id,
  name: z.string(),
  cost: z.number().int().positive(),
  costGrowth: z.number().min(1),
  /** D454: the branch of the permanent tree it sits on. */
  branch: IdleHeritageBranch,
  /** D454: its tier in the branch, from 1, opened by Heritage spent in the branch. */
  tier: z.number().int().positive(),
  /**
   * D454: a fork: two perks side by side in one tier. Owning one closes the other for the rest
   * of the generation; passing the world on opens both again.
   */
  choice: Id.optional(),
  /**
   * D336: from this many levels owned, each level costs `growth` times the last instead, so a
   * large world's Heritage left over after a festival still buys something.
   */
  lateGrowth: z.object({ from: z.number().int().positive(), growth: z.number().min(1) }).optional(),
  /** The top level. Absent: no top level, so Heritage always has a use (D250). */
  maxLevel: z.number().int().positive().optional(),
  /** A multiplier, applied once per level owned. */
  effect: IdleEffectSpec.optional(),
  /** Coins every new year starts with, per level owned. */
  startCoins: z.number().int().positive().optional(),
  /** Producers every new year starts with unlocked, one owned. */
  startProducers: z.array(Id).optional(),
  /** Producers every new year starts with a hand on. */
  startHands: z.array(Id).optional(),
  /** Counts as this many more festivals per level when a producer needs festivals (D250). */
  festivals: z.number().int().positive().optional(),
  /** D355: can be bought only once this many festivals have been held in this generation. */
  needsFestivals: z.number().int().nonnegative().optional(),
  /** D367: can be bought only once this perk is owned, as a hand on a crop needs the crop first. */
  needsPerk: Id.optional(),
});

/**
 * A Lineage trait (D257), bought with Lineage when the world is passed on and kept for good. Each
 * changes a rule rather than only multiplying.
 */
export const IdleTrait = z.object({
  id: Id,
  name: z.string(),
  /** Lineage for the first level; each level after costs `costGrowth` times the last. */
  cost: z.number().int().positive(),
  costGrowth: z.number().min(1),
  /** The top level. Absent: no top level, so Lineage always has a use (D264). */
  maxLevel: z.number().int().positive().optional(),
  /** Every producer unlocked comes with its hand hired. */
  hands: z.boolean().optional(),
  /**
   * Producers every year starts with unlocked and run by a hand: the first `startFirst` at
   * level 1 and one more for each level after (D264).
   */
  startProducers: z.array(Id).optional(),
  startFirst: z.number().int().positive().optional(),
  /** The barn level a new generation starts at, one higher for each level after the first (D264). */
  startBarnLevel: z.number().int().positive().optional(),
  /**
   * Each level keeps this share of the last generation's Heritage that the levels before it did
   * not, so the share kept nears all of it but never reaches it (D263, D264).
   */
  keepHeritage: z.number().gt(0).lt(1).optional(),
  /** Per level, every producer's cost growth is this much lower, so late units cost less (D264). */
  costGrowthCut: z.number().positive().optional(),
  /** D301: opens this piece of land beside the world, by `map.lands` id, with its producers. */
  opensLand: Id.optional(),
  /**
   * D477: each level adds this to what the Heritage festivals pay from earnings is multiplied by,
   * for good, so passing on is worth it: level n pays 1 + this x n times. Added, never compounded.
   */
  heritageGain: z.number().positive().optional(),
});

export const IdleBarnLevel = z.object({
  /** How many hours of away time the barn holds at this level. */
  hours: z.number().positive(),
  /** Coins to reach this level from the one before. 0 for level 1. */
  cost: Coins,
  /**
   * D361: the share of income past `hours` the barn still keeps, however long the player is away.
   * Absent: none, and earning stops when the barn is full.
   */
  overflow: z.number().gt(0).lt(1).optional(),
});

/** A rectangle of map tiles (D267). */
const IdleMapRect = z.object({
  x: z.number().int(),
  y: z.number().int(),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
});

/**
 * D348: the player's words in a theme, by role. A screen says `terms.festival` where the farm
 * says "festival", so a theme renames a thing without a code change. `decorationKinds` labels
 * each decoration kind (`building`, `roof` and so on) as the decorations screen lists it.
 */
export const IdleTerms = z.object({
  world: z.string().min(1),
  worlds: z.string().min(1),
  festival: z.string().min(1),
  festivals: z.string().min(1),
  /** D371: the verb for putting the festival on: "hold", "run". */
  hold: z.string().min(1),
  heritage: z.string().min(1),
  barn: z.string().min(1),
  hand: z.string().min(1),
  hands: z.string().min(1),
  almanac: z.string().min(1),
  almanacPage: z.string().min(1),
  region: z.string().min(1),
  lineage: z.string().min(1),
  generation: z.string().min(1),
  passOn: z.string().min(1),
  trimmings: z.string().min(1),
  coins: z.string().min(1),
  /** D371: what a year starts again with, beside the coins: "crops", "hardware". */
  crops: z.string().min(1),
  harvest: z.string().min(1),
  tap: z.string().min(1),
  track: z.string().min(1),
  yard: z.string().min(1),
  visitor: z.string().min(1),
  /** D380: the plural, for "two visitors called". */
  visitors: z.string().min(1),
  market: z.string().min(1),
  weather: z.string().min(1),
  luckyFind: z.string().min(1),
  /** D364: the plural, for a list or a sentence that counts more than one. */
  luckyFinds: z.string().min(1),
  marks: z.string().min(1),
  breeds: z.string().min(1),
  tabWorld: z.string().min(1),
  tabAlmanac: z.string().min(1),
  tabFestival: z.string().min(1),
  tabRegion: z.string().min(1),
  /** D364: the Region tab's orders card title: "Village orders", "Client orders". */
  ordersTitle: z.string().min(1),
  /** D364: the orders card before any have come: "The village sends its first orders tomorrow." */
  ordersFirstDay: z.string().min(1),
  /** D364: one, lower case: "rosette", "badge". Capped and pluralised in code. */
  rosette: z.string().min(1),
  /** D364: the weekly goal card's title: "Weekly goal", "Weekly target". */
  weeklyGoal: z.string().min(1),
  decorationKinds: z.record(z.string(), z.string().min(1)),
});

/** D348: the kinds of bonus a theme can offer. One left out of `bonuses.kinds` never comes. */
export const IDLE_BONUS_KINDS = ['market_day', 'lucky_find', 'visitor', 'weather'] as const;
export const IdleBonusKind = z.enum(IDLE_BONUS_KINDS);

/** D348: the collection book's categories. One left out of `collection.categories` is not listed. */
export const IDLE_COLLECTION_CATEGORIES = [
  'breeds',
  'tiers',
  'marks',
  'monuments',
  'seasons',
  'visitors',
  'prizes',
  'seasonal',
  'rosettes',
  'sets',
] as const;
export const IdleCollectionCategory = z.enum(IDLE_COLLECTION_CATEGORIES);

/**
 * D348: the ground a theme's map is laid on. `water`: `edge` runs a river (or a stream) along
 * one side outside the fence (D287); `none` has no water outside it, and a kind's `riverSides`
 * only set which side its fields fill from. `scatter`: the kinds of thing that stand singly
 * outside the fence (trees in the farm theme), which the map's skin picks between; empty, nothing
 * stands there. `track`: the barn-to-plot track and its bridges (D287), in `track.surfaces`;
 * false, there is none.
 */
export const IdleTerrain = z.object({
  water: z.enum(['edge', 'none']),
  scatter: z.array(Id),
  track: z.boolean(),
});

export const IdleContentSchema = z
  .object({
    /** D348: the theme this content is: its id, and its name as the player reads it. */
    theme: z.object({
      id: Id,
      name: z.string().min(1),
      /** A one-line description for the player, shown in the theme picker. */
      description: z.string().min(1),
      /** D365: a building row's icon (a UI glyph name, not a sprite), never the world's barn. */
      buildingIcon: z.string().min(1),
    }),
    /** D348: what the theme's money is called, its short form after a number (`300c`), its icon. */
    currency: z.object({ name: z.string().min(1), short: z.string().min(1), icon: Id }),
    terms: IdleTerms,
    /** D454: each tree branch's name in the theme, by branch id: the Almanac's and the permanent tree's. */
    branches: z.record(z.string(), z.string().min(1)),
    /** D454: each producer group's name in the theme, plural and lower case ("crops"), by group. */
    groups: z.record(IdleProducerGroup, z.string().min(1)).optional(),
    startCoins: z.number().int().nonnegative(),
    /** D266: the first run suggests one of these, drawn at random, as the new world's name. */
    worldNames: z.array(z.string().trim().min(1).max(40)).min(1),
    /** D410: the name a new world gets when none is asked for, then `name 2` and on. */
    homeName: z.string().trim().min(1).max(40),
    /** D337: how many playable idle worlds a player may keep. Old classic worlds do not count. */
    worldsPerPlayer: z.number().int().positive(),
    /**
     * In the order they are shown. The next two not yet unlocked are shown, and either can be
     * unlocked, in any order (D275).
     */
    producers: z.array(IdleProducer).min(1),
    /** One building per chain, never a web. */
    chains: z.array(IdleChain),
    /** The Almanac: upgrades bought with coins, kept for the year (D244). */
    upgrades: z.array(IdleUpgrade),
    /** Milestones that pay a small multiplier for the rest of the year when met (D244). */
    goals: z.array(IdleGoal),
    /**
     * D334: the year's goals grow with the world. A goal but a chain has up to `tiers` tiers, each
     * met on its own; its targets rise with the festivals held this generation, and a generation
     * after the first counts as `perGeneration` more festivals for each before it.
     */
    goalScale: z
      .object({
        tiers: z.number().int().positive(),
        /** A goal gains a tier for each this many festivals held, up to `tiers`. */
        festivalsPerTier: z.number().int().positive(),
        perGeneration: z.number().nonnegative(),
        /** own: `count` + `perFestival` x festivals, times `tierStep` for each tier after the first. */
        own: z.object({ perFestival: z.number().nonnegative(), tierStep: z.number().gt(1) }),
        /** earn: `coins` x `perFestival` ^ festivals, times `tierStep` for each tier after the first. */
        earn: z.object({ perFestival: z.number().min(1), tierStep: z.number().gt(1) }),
        /** hands: as own. */
        hands: z.object({ perFestival: z.number().nonnegative(), tierStep: z.number().gt(1) }),
        /**
         * A tier after the first pays this share of the goal's reward (and of its Heritage, rounded
         * down). D392: at least 1, so a later stage never pays less than an earlier one.
         */
        laterTierReward: z.number().min(1),
      })
      .optional(),
    /**
     * D334: long goals, met once a generation and kept through its festivals; their rewards
     * last until the world is passed on. Each tier of `targets` is met on its own.
     */
    longGoals: z.array(IdleGoal).default([]),
    /** Level 1 is the first entry. */
    barn: z.array(IdleBarnLevel).min(1),
    /**
     * D352: hands and unlocks are priced from the crop and the world's stage, never from income, so
     * no bonus moves a price.
     */
    prices: z.object({
      /** A hand costs the price of its producer's next this many units, or its own price if more. */
      handUnits: z.number().int().positive(),
      /** A hand never costs less than this share of its producer's next unit, whatever cuts it (D333). */
      handUnitShare: z.number().nonnegative(),
      /**
       * An unlock costs its own price times this for each festival held this generation, so it is
       * set when the year starts and holds all year.
       */
      unlockPerFestival: z.number().min(1),
      /**
       * D367: every unit costs its own price times this for each festival held this generation,
       * as an unlock does, so the first units of a year are a choice too. Set when the year
       * starts and fixed all year.
       */
      unitPerFestival: z.number().min(1),
      /**
       * D386: unlock and hand prices are multiplied by the Heritage income multiplier the year
       * started with, to this power, so they keep pace with the world's stage without following
       * income. Fixed for the whole year. Absent: 0, no multiplier.
       */
      heritagePower: z.number().min(0).optional(),
    }),
    festival: z.object({
      /**
       * D276: a festival held and its new year not started is started on the next check-in after
       * the player has been away this long, so a world is never left waiting.
       */
      startAfterAwayMs: z.number().int().positive(),
      /**
       * Heritage = floor(this x earnings ^ `heritagePower`) (D245, D299). At a power of 0.25,
       * sixteen times the earnings doubles it, so Heritage grows steadily with the orders of
       * magnitude a world climbs, not with its coins.
       */
      heritagePerRootCoin: z.number().positive(),
      heritagePower: z.number().gt(0).max(1).default(0.5),
      /**
       * D299: goals' Heritage is at most this share of a festival's whole Heritage, so a
       * festival is never paid mostly by goals. Absent: no cap.
       */
      goalShare: z.number().min(0).lt(1).optional(),
      /**
       * What Heritage is paid from (D249). year: the square root of the year's earnings. generation:
       * the square root of everything earned this generation, less the Heritage festivals have
       * already paid for it, so splitting the same earnings into more years pays no more.
       */
      heritageFrom: z.enum(['year', 'generation']).default('year'),
      /**
       * D321: a festival pays from earnings at most this many times what the generation's last
       * festival paid from earnings (or what makes it worth holding, if more). The rest waits
       * for the next festival. Absent: no cap.
       */
      maxStep: z.number().gt(1).optional(),
      /** A festival must pay at least this share of the Heritage held (D264). */
      minShare: z.number().min(0).optional(),
      /** The year must earn this much before the festival can be held. */
      minYearEarned: z.number().positive(),
      /**
       * D321: the income bonus from Heritage earned is (1 + H / this) to a power solved from
       * `ready`, so past this much Heritage each doubling lifts income by the same share.
       */
      heritageBonusScale: z.number().positive(),
      /**
       * D321, D322: when the festival is worth holding. It is ready once it lifts all income by at
       * least `lift` (x1.25: a quarter more) and the year has run `minYearMs`. A year that has run `maxYearMs` is ready whatever
       * it earned: the festival then pays at least the Heritage for `lift`, so a worthwhile
       * festival is never further off than that.
       */
      ready: z.object({
        lift: z.number().gt(1),
        /**
         * The times the generation's earnings must grow for a festival to give `lift`: the
         * target the Heritage bonus's power is solved from.
         */
        earnings: z.number().gt(1),
        /** Never ready before the year has run this long, so years are hours, not minutes. */
        minYearMs: z.number().int().nonnegative(),
        /**
         * D383: `minYearMs` grows this many times for each festival held in this generation,
         * never past `maxYearMs`. Absent: 1, a fixed floor.
         */
        floorGrowth: z.number().min(1).optional(),
        maxYearMs: z.number().int().positive(),
      }),
      /** The marks festivals leave on the map, in order (D245). After the last, they start again. */
      marks: z.array(Id).min(1),
    }),
    /** The festival shop: the permanent tree (D454). */
    perks: z.array(IdlePerk),
    /**
     * D454: the trees' rules. An Almanac tier opens once `opensShare` of the tier before it is
     * bought, rounded up (two of three, three of six). A permanent tier opens once the Heritage
     * spent in its branch reaches `perkTiers[branch][tier - 1]`; the first is 0.
     */
    tree: z.object({
      opensShare: z.number().gt(0).max(1),
      perkTiers: z.record(IdleHeritageBranch, z.array(z.number().nonnegative()).min(1)),
      /**
       * D457: the Almanac tiers each branch opens in the first generation. Each generation after
       * opens one more, up to the branch's last: tier `firstTiers[branch] + g - 1` in generation g.
       */
      firstTiers: z.record(IdleAlmanacBranch, z.number().int().positive()),
      /**
       * D457: the mastery step at the foot of each Almanac branch. It opens once every step of the
       * branch's last open tier is bought (or closed by its fork) this year, and levels with no
       * top; the levels reset with the year. Each level gives the branch's `effects` once more,
       * compounded. Level n (from 0) costs `first` x `growth` ^ n times the dearest step of that
       * last open tier, at today's price.
       */
      mastery: z.object({
        first: z.number().positive(),
        growth: z.number().gt(1),
        effects: z.record(
          IdleAlmanacBranch,
          z.object({
            stat: z.enum(['speed', 'value', 'tap', 'hand']),
            factor: z.number().positive(),
          }),
        ),
      }),
    }),
    /**
     * D454 step 3: arranging the land. From generation `moveFrom` a plot may be moved, free. Each
     * of the theme's `links` whose two plots sit side by side (across one lane, never corner to
     * corner) pays the producer it goes to `link` more value. The hands' building is the yard:
     * every open plot its reach touches (a ring `yardReach` tiles from the yard's middle) earns
     * `yard` more. A producer's links and the yard add up to at most `cap`. Two plots swap places
     * only when each place is at least `fit` of the other plot's area. The numbers are shared;
     * the pairs are the theme's own, and a theme with none has no links.
     */
    arrange: z
      .object({
        moveFrom: z.number().int().positive(),
        link: z.number().min(0),
        yard: z.number().min(0),
        yardReach: z.number().min(0),
        cap: z.number().min(0),
        fit: z.number().gt(0).max(1),
        links: z.array(IdleLink).default([]),
        /** The theme's word for a group's plot, for the arrange dock ("field", "pen"). */
        places: z.record(IdleProducerGroup, z.string().min(1)).optional(),
      })
      .default({ moveFrom: 2, link: 0, yard: 0, yardReach: 0, cap: 0, fit: 1, links: [] }),
    /** Passing the world on (D257). */
    lineage: z.object({
      /** Festivals this generation must hold before the world can be passed on. */
      minFestivals: z.number().int().positive(),
      /** Lineage = floor(perRootHeritage x the square root of Heritage earned this generation). */
      perRootHeritage: z.number().positive(),
      /**
       * Passing on costs something (D264): a new generation's festivals pay only once its own
       * Heritage passes this share of the last generation's.
       */
      heritageFloor: z.number().min(0).max(1),
      /**
       * D325: Lineage each deed of ordinary play adds to the meter, paid in whole Lineage when
       * the world is passed on: an order sent, a visitor met, a chain built, a festival held.
       */
      meter: z.object({
        order: z.number().nonnegative(),
        visitor: z.number().nonnegative(),
        chain: z.number().nonnegative(),
        festival: z.number().nonnegative(),
      }),
      traits: z.array(IdleTrait),
    }),
    /**
     * D277: a tap on a hand-run producer takes `share` of a bar off its current bar, times every
     * tap effect. Taps take at most `maxShare` of any one bar. At most a half, so tapping as fast
     * as anyone can never earns more on top than being away earns in the same time (D242).
     */
    tap: z
      .object({
        share: z.number().positive(),
        maxShare: z.number().positive().max(0.5),
      })
      .refine((t) => t.share <= t.maxShare, 'tap share above its cap'),
    /**
     * D267: the world's land. The default plan, which a world with no seed keeps: the yard, each
     * producer's plot and the first festival mark, in map tiles. A seed may flip it east to west
     * and adds trees and a stream outside it, up to `margin` tiles out.
     */
    map: z.object({
      terrain: IdleTerrain.default({ water: 'edge', scatter: ['tree'], track: true }),
      width: z.number().int().positive(),
      height: z.number().int().positive(),
      margin: z.number().int().min(2),
      yard: IdleMapRect,
      /** Where the first mark stands; marks run east in rows of `perRow`, each row behind the last. */
      marks: z.object({
        x: z.number().int(),
        y: z.number().int(),
        perRow: z.number().int().positive(),
      }),
      plots: z.record(Id, IdleMapRect),
      /** A seeded world has between `min` and `max` trees. */
      trees: z.object({ min: z.number().int().nonnegative(), max: z.number().int().nonnegative() }),
      /** The share of seeded worlds with a stream. */
      streamChance: z.number().min(0).max(1),
      /**
       * D440: a seeded world of a kind leaves a lone tile between two fields with chance
       * `chance`, and a second with chance `second` once it has one. Each takes a filler.
       */
      fillers: z
        .object({ chance: z.number().min(0).max(1), second: z.number().min(0).max(1) })
        .default({ chance: 0, second: 0 }),
      /**
       * D301: pieces of land beside the world that a Lineage trait opens. Each is `w` by `h` tiles,
       * its producers' plots placed from its own back corner. The seed sets the side and the place.
       */
      lands: z
        .array(
          z.object({
            id: Id,
            name: z.string(),
            w: z.number().int().positive(),
            h: z.number().int().positive(),
            plots: z.record(Id, IdleMapRect),
          }),
        )
        .default([]),
      /**
       * D344: the kinds of world a new world's seed picks from, by `weight`. Each lays its own
       * outline and fields: the world is `width` tiles wide (an inclusive low and high), its
       * fields packed in bands of the heights in `bands`, each field about its plan size
       * times `fieldScale`, give or take `jitter`. A band is set in from a side the river does
       * not run along with chance `notch`, and the last band's spare ground is left outside
       * the fence, so the outline is not always a rectangle. `riverSides` weights the river's
       * side (west, east, front). `woods` is how much likelier a tree is on the world's wooded
       * side; `pond` the chance of a pond among the fields. `scenery` scales the country round
       * it: the 3D hills, the neighbours' copses and grazing, how near the village is (below 1
       * is nearer) and how many more houses it has. Empty: every world keeps the plan above.
       */
      kinds: z
        .array(
          z.object({
            id: Id,
            name: z.string(),
            weight: z.number().positive(),
            width: z.tuple([z.number().int().min(12), z.number().int().min(12)]),
            bands: z.array(z.number().int().min(3).max(5)).nonempty(),
            fieldScale: z.number().positive(),
            jitter: z.number().min(0).max(0.5),
            notch: z.number().min(0).max(1),
            riverSides: z.tuple([
              z.number().nonnegative(),
              z.number().nonnegative(),
              z.number().nonnegative(),
            ]),
            trees: z.object({
              min: z.number().int().nonnegative(),
              max: z.number().int().nonnegative(),
            }),
            woods: z.number().min(1),
            pond: z.number().min(0).max(1),
            scenery: z.object({
              hills: z.number().nonnegative(),
              copse: z.number().nonnegative(),
              pasture: z.number().nonnegative(),
              village: z.number().positive(),
              houses: z.number().int().nonnegative(),
            }),
          }),
        )
        .default([]),
      /**
       * D344: the named traits a world's land can have, after its kind's own name. A world shows
       * the first that hold, in this order, up to `maxTraits` with its kind; a draw with none
       * is redrawn. `rule` says what `at` is measured against: trees at least or at most,
       * the biggest field's tiles at least, the smallest field's at most, a pond, the river's
       * bends at least, tiles cut from the outline at least, or a field this many times as long
       * as it is wide.
       */
      traits: z
        .array(
          z.object({
            id: Id,
            name: z.string(),
            rule: z.enum([
              'treesAtLeast',
              'treesAtMost',
              'biggestField',
              'smallestField',
              'pond',
              'riverBends',
              'cutTiles',
              'longField',
            ]),
            at: z.number().nonnegative(),
          }),
        )
        .default([]),
      /** D344: the most traits a world shows, its kind's name counted. */
      maxTraits: z.number().int().min(2).default(3),
    }),
    /** Bonuses, never punishments (D244). Offers come only while the player is here. */
    bonuses: z.object({
      /** D348: the kinds this theme offers; one left out never comes and pays nothing. */
      kinds: z.array(IdleBonusKind).default([...IDLE_BONUS_KINDS]),
      /** The player counts as here for this long after their last visit or action. */
      presentMs: z.number().int().positive(),
      /** A market day is offered about every `everyMs`, give or take `jitterMs`. */
      marketDay: z.object({
        everyMs: z.number().int().positive(),
        jitterMs: z.number().int().nonnegative(),
        /** How long the offer stays to be claimed. */
        offerMs: z.number().int().positive(),
        /** How long the boost lasts once claimed, and what it multiplies all income by. */
        durationMs: z.number().int().positive(),
        factor: z.number().gt(1),
      }),
      /** A lucky find appears on the map about every `everyMs`, give or take `jitterMs`. */
      luckyFind: z.object({
        everyMs: z.number().int().positive(),
        jitterMs: z.number().int().nonnegative(),
        /** How long it stays on the map. */
        lastsMs: z.number().int().positive(),
        /** It pays this many seconds of the world's income. */
        worthSeconds: z.number().positive(),
      }),
      /** Good weather may come on a check-in after an absence of at least `minAwayMs`. */
      weather: z.object({
        minAwayMs: z.number().int().positive(),
        chance: z.number().min(0).max(1),
        durationMs: z.number().int().positive(),
        factor: z.number().gt(1),
      }),
      /**
       * D289: a visitor calls at the gate about every `everyMs`, give or take `jitterMs`, while the
       * player is here, and stays `lastsMs`. Tapped, it pays `worthSeconds` of the world's income;
       * missed, it simply goes. Which visitor comes is seeded; their ids are kept for the collection.
       */
      visitors: z.object({
        everyMs: z.number().int().positive(),
        jitterMs: z.number().int().nonnegative(),
        lastsMs: z.number().int().positive(),
        worthSeconds: z.number().positive(),
        /**
         * D330: a kind's share of who calls, weighted against the others' (default 1), so a rare
         * visitor can be given a low `chance` without changing how much it pays.
         */
        kinds: z
          .array(z.object({ id: Id, name: z.string(), chance: z.number().positive().optional() }))
          .min(1),
      }),
    }),
    /**
     * D287: the world track's surface rises with the producers owned this year: the last entry
     * whose `owned` the world has reached. The first must be at 0.
     */
    track: z.object({
      surfaces: z
        .array(z.object({ id: Id, owned: z.number().int().nonnegative() }))
        .min(1)
        .refine((s) => s[0]!.owned === 0, 'the first surface must be at 0')
        .refine((s) => s.every((x, i) => i === 0 || x.owned > s[i - 1]!.owned), 'must rise'),
    }),
    /**
     * D288: the seasons of a festival year, in order. A season runs from its `from` (0 to 1 of
     * the year's way to the point the festival is worth holding) to the next one's. The year's
     * progress is (coins earned / coins needed) to the power `power`. Looks only, never earnings.
     */
    seasons: z.object({
      power: z.number().positive().max(1),
      list: z
        .array(z.object({ id: Id, name: z.string(), from: z.number().min(0).lt(1) }))
        .min(1)
        .refine((s) => s[0]!.from === 0, 'the first season must start at 0')
        .refine((s) => s.every((x, i) => i === 0 || x.from > s[i - 1]!.from), 'must rise'),
    }),
    /** D288: day and night on a cycle of `cycleMs` from the server's clock; phases as seasons. */
    day: z.object({
      cycleMs: z.number().int().positive(),
      phases: z
        .array(z.object({ id: Id, from: z.number().min(0).lt(1) }))
        .min(1)
        .refine((s) => s[0]!.from === 0, 'the first phase must start at 0')
        .refine((s) => s.every((x, i) => i === 0 || x.from > s[i - 1]!.from), 'must rise'),
    }),
    /**
     * D290, D330: what the collection book lists besides the festival marks, seasons, visitors
     * and weekly prizes. A breed is found once its producer has reached `tier` milestones; a
     * building tier once any producer has; a monument-ground style once a festival has built one
     * (D327). A decoration set (one whole kind) and a rosette are not named here: a set's kinds
     * come from `decorations` itself, and a rosette is named plainly from its id, since nothing
     * earns one yet (D326, a hook; the matching state field is `permanent.rosettes`). Ids are
     * stable: the collection keeps them for good.
     */
    collection: z.object({
      /** D348: the categories this theme's book lists, in the book's order. */
      categories: z.array(IdleCollectionCategory).default([...IDLE_COLLECTION_CATEGORIES]),
      breeds: z.array(
        z.object({ id: Id, name: z.string(), producer: Id, tier: z.number().int().min(0) }),
      ),
      tiers: z.array(z.object({ id: Id, name: z.string(), tier: z.number().int().min(0) })),
      /** D330: a mark's plain name, by its id in `festival.marks`. A mark missing here shows its
       * id as words, not raw ("standing stone" for `standing_stone`). */
      markNames: z.record(Id, z.string()).default({}),
      monuments: z.array(z.object({ id: Id, name: z.string() })).default([]),
      rosettes: z.array(z.object({ id: Id, name: z.string() })).default([]),
    }),
    /**
     * D291, D331: decorations restyle the world and change no numbers. Bought with Trimmings and
     * kept for good. Each kind's first entry is its default: free, open at once, and what a world
     * that has bought none shows. `festivals` is how many festivals must have been held before it
     * can be bought. `event` makes it seasonal: it can be bought only while that weekly event
     * runs, and comes back each time the event does.
     */
    decorations: z
      .array(
        z.object({
          id: Id,
          kind: z.enum(['building', 'roof', 'path', 'fence', 'hedge', 'gate', 'vehicle', 'worker']),
          name: z.string(),
          cost: z.number().int().nonnegative(),
          festivals: z.number().int().nonnegative().default(0),
          event: Id.optional(),
        }),
      )
      .min(1),
    /**
     * D331: Trimmings, the currency decorations cost. Only ever earned, never sold, never spent
     * on anything that earns. The daily gift pays the `n`th entry of `gift` on the `n`th day in a
     * row (past the last, the last); `allOrders` is paid once a day when all its orders are sent;
     * a tapped visitor or lucky find pays its own; a weekly event won pays `event`.
     */
    trimmings: z.object({
      gift: z.array(z.number().int().nonnegative()).min(1),
      allOrders: z.number().int().nonnegative(),
      visitor: z.number().int().nonnegative(),
      luckyFind: z.number().int().nonnegative(),
      event: z.number().int().nonnegative(),
    }),
    /**
     * D304: reasons to come back each day. Days run by the server's clock from midnight UTC,
     * `dayMs` long; a week starts on the day `weekStartDay` days after 1 January 1970 (a
     * Thursday, so 4 is a Monday). Every reward is seconds of the world's income, as a lucky find
     * is (D254), with a floor for a new world; together a day's never outearns a full barn (D242).
     */
    /**
     * D310: web push. At most one message every `gapHours` to a phone, and none from
     * `quietFromHour` up to `quietUntilHour` on the phone's own clock.
     */
    push: z.object({
      gapHours: z.number().positive(),
      quietFromHour: z.number().int().min(0).max(23),
      quietUntilHour: z.number().int().min(0).max(23),
      /**
       * D400: a world whose owner has been away this long stops its hourly alarm, so no
       * region report and no push check, until any call to it wakes it.
       */
      sleepAfterHours: z.number().positive(),
    }),
    /** D332: how long dismissing the install prompt hides it for. */
    install: z.object({
      snoozeDays: z.number().positive(),
    }),
    daily: z.object({
      dayMs: z.number().int().positive(),
      weekStartDay: z.number().int().min(0).max(6),
      /**
       * Three orders a day, one for each size: send what `seconds` of making the producer
       * makes, for `worthSeconds` of income. `goods` names what a producer sends ("eggs"); a
       * producer not listed sends its own name.
       */
      orders: z.object({
        sizes: z
          .array(z.object({ seconds: z.number().positive(), worthSeconds: z.number().positive() }))
          .min(1),
        minCoins: z.number().int().positive(),
        goods: z.record(z.string(), z.string()),
      }),
      /**
       * A gift on the first visit each day, never on the day the world was made. The `n`th day in
       * a row pays the `n`th entry's seconds of income; past the last, the last. A missed day
       * starts the run again.
       */
      gift: z.object({
        worthSeconds: z.array(z.number().positive()).min(1),
        minCoins: z.number().int().positive(),
      }),
    }),
    /**
     * D304: a themed week, in turn from this list by the week's number. Its goal is orders
     * filled that week, or days visited that week; met, it gives its prize, a collection entry
     * that can be had only that week.
     */
    events: z
      .array(
        z.object({
          id: Id,
          name: z.string(),
          goal: z.object({
            kind: z.enum(['orders', 'days']),
            count: z.number().int().positive(),
          }),
          prize: z.object({ id: Id, name: z.string() }),
        }),
      )
      .min(1),
    /**
     * D326: a rosette, a rare and lasting reward for turning up most weeks, like Egg, Inc.'s
     * monthly Prophecy Egg. Earned by winning the weekly event (D304) in `need` of the last
     * `window` weeks; a missed week keeps what is already earned, and the window starts fresh
     * once a rosette is won. Each one raises all income by `factor` for good, compounding, kept
     * across festivals and generations, up to `cap` rosettes.
     */
    rosettes: z
      .object({
        window: z.number().int().positive(),
        need: z.number().int().positive(),
        factor: z.number().min(1),
        cap: z.number().int().positive(),
      })
      .refine((r) => r.need <= r.window, 'need must be at most window'),
  })
  .superRefine((c, ctx) => {
    const ids = new Set<string>();
    c.producers.forEach((p, i) => {
      if (ids.has(p.id)) ctx.addIssue({ code: 'custom', message: `duplicate producer ${p.id}` });
      ids.add(p.id);
      const owned = p.milestones.map((m) => m.owned);
      if (owned.some((o, j) => j > 0 && o <= (owned[j - 1] ?? 0)))
        ctx.addIssue({ code: 'custom', path: ['producers', i], message: 'milestones must rise' });
    });
    const perks = new Set(c.perks.map((k) => k.id));
    for (const ch of c.chains) {
      if (ch.needsPerk !== undefined && !perks.has(ch.needsPerk))
        ctx.addIssue({
          code: 'custom',
          message: `chain ${ch.id} needs unknown perk ${ch.needsPerk}`,
        });
      if (!ids.has(ch.producer))
        ctx.addIssue({
          code: 'custom',
          message: `chain ${ch.id} lifts unknown producer ${ch.producer}`,
        });
    }
    const chains = new Set(c.chains.map((ch) => ch.id));
    for (const ch of c.chains) {
      const first = c.chains.find((x) => x.id === ch.needsChain);
      if (ch.needsChain !== undefined && first?.producer !== ch.producer)
        ctx.addIssue({
          code: 'custom',
          message: `chain ${ch.id} follows ${ch.needsChain}, which is not a chain on ${ch.producer}`,
        });
    }
    const bad = (message: string) => ctx.addIssue({ code: 'custom', message });
    for (const u of c.upgrades) {
      if (u.needsPerk !== undefined && !perks.has(u.needsPerk))
        bad(`upgrade ${u.id} needs unknown perk ${u.needsPerk}`);
      for (const p of [u.needsProducer, u.effect.producer, u.effect.per?.producer])
        if (p !== undefined && !ids.has(p)) bad(`upgrade ${u.id} names unknown producer ${p}`);
      if (u.effect.group !== undefined && u.effect.producer !== undefined)
        bad(`upgrade ${u.id} names both a producer and a group`);
      // D454: a group effect needs the theme to name the group and put producers in it.
      const g = u.effect.group;
      if (g !== undefined && (!c.groups?.[g] || !c.producers.some((p) => p.group === g)))
        bad(`upgrade ${u.id} lifts group ${g}, which the theme does not name and fill`);
    }
    // D454 step 3: each link joins two known producers, once.
    const pairs = new Set<string>();
    for (const l of c.arrange.links) {
      for (const p of [l.from, l.to]) if (!ids.has(p)) bad(`link names unknown producer ${p}`);
      const key = [l.from, l.to].sort().join('+');
      if (l.from === l.to || pairs.has(key)) bad(`link ${l.from} to ${l.to} is not a new pair`);
      pairs.add(key);
    }
    // D454: a theme that groups its producers names each group it uses.
    for (const p of c.producers)
      if (p.group !== undefined && !c.groups?.[p.group])
        bad(`producer ${p.id} is in group ${p.group}, which the theme does not name`);
    // D454: each branch is tiers 1, 2, 3 and on; an Almanac tier holds three or six steps; a
    // fork is two steps of one tier; and a tier can always be opened past, whichever side of
    // each fork is taken.
    type Step = { id: string; branch: string; tier: number; choice?: string | undefined };
    const trees: [string, Step[], readonly string[]][] = [
      ['upgrade', c.upgrades, IDLE_ALMANAC_BRANCHES],
      ['perk', c.perks, IDLE_HERITAGE_BRANCHES],
    ];
    for (const [kind, steps, branches] of trees) {
      for (const b of branches) {
        if (!c.branches[b]) bad(`branches has no word for ${b}`);
        const on = steps.filter((x) => x.branch === b);
        const top = Math.max(0, ...on.map((x) => x.tier));
        for (let t = 1; t <= top; t++) {
          const tier = on.filter((x) => x.tier === t);
          if (tier.length === 0) bad(`${kind} branch ${b} has no tier ${t}`);
          if (kind !== 'upgrade') continue;
          if (tier.length !== 3 && tier.length !== 6)
            bad(`upgrade branch ${b} tier ${t} has ${tier.length} steps, not 3 or 6`);
          const forks = new Set(tier.flatMap((x) => (x.choice ? [x.choice] : [])));
          if (tier.length - forks.size < Math.ceil(tier.length * c.tree.opensShare))
            bad(`upgrade branch ${b} tier ${t} cannot be opened past`);
        }
        if (kind === 'perk') {
          const at = c.tree.perkTiers[b as IdleHeritageBranchT] ?? [];
          if (at.length < top) bad(`tree.perkTiers.${b} has fewer than ${top} tiers`);
          if (at[0] !== 0) bad(`tree.perkTiers.${b} must start at 0`);
          if (at.some((n, i) => i > 0 && n < at[i - 1]!)) bad(`tree.perkTiers.${b} must rise`);
        }
      }
      const forkOf = new Map<string, Step[]>();
      for (const x of steps)
        if (x.choice) forkOf.set(x.choice, [...(forkOf.get(x.choice) ?? []), x]);
      for (const [f, sides] of forkOf) {
        const [a, z2] = sides;
        if (sides.length !== 2 || a!.branch !== z2!.branch || a!.tier !== z2!.tier)
          bad(`${kind} fork ${f} must be two steps of one tier`);
      }
    }
    // D457: every Almanac branch says how many tiers its first generation opens and what its
    // mastery step lifts.
    for (const b of IDLE_ALMANAC_BRANCHES) {
      if (c.tree.firstTiers[b] === undefined) bad(`tree.firstTiers has no ${b}`);
      if (!c.tree.mastery.effects[b]) bad(`tree.mastery.effects has no ${b}`);
    }
    for (const u of c.upgrades)
      if (u.needsOwned !== undefined && !ids.has(u.needsOwned.producer))
        bad(`upgrade ${u.id} needs unknown producer ${u.needsOwned.producer}`);
    const yearKinds = new Set(['own', 'earn', 'chain', 'hands']);
    const goalIds = new Set<string>();
    for (const [long, list] of [
      [false, c.goals],
      [true, c.longGoals],
    ] as const)
      for (const g of list) {
        if (goalIds.has(g.id)) bad(`goal ${g.id} is listed twice`);
        goalIds.add(g.id);
        if (g.id.includes('@')) bad(`goal ${g.id}: an id may not hold @`);
        if (g.producer !== undefined && !ids.has(g.producer)) bad(`goal ${g.id}: unknown producer`);
        if (g.chain !== undefined && !chains.has(g.chain)) bad(`goal ${g.id}: unknown chain`);
        const need = {
          own: g.producer && g.count,
          earn: g.coins,
          chain: g.chain,
          hands: g.count,
          festivals: g.targets ?? g.count,
          perks: g.targets ?? g.count,
          lifetime: g.targets ?? g.coins,
        };
        if (!need[g.kind]) bad(`goal ${g.id} lacks what its kind needs`);
        if (yearKinds.has(g.kind) === long)
          bad(`goal ${g.id}: kind ${g.kind} is not a ${long ? 'long' : 'year'} goal`);
      }
    for (const k of c.perks)
      for (const p of [...(k.startProducers ?? []), ...(k.startHands ?? []), k.effect?.producer])
        if (p !== undefined && !ids.has(p)) bad(`perk ${k.id} names unknown producer ${p}`);
    for (const k of c.perks)
      if (k.needsPerk !== undefined && (!perks.has(k.needsPerk) || k.needsPerk === k.id))
        bad(`perk ${k.id} needs unknown perk ${k.needsPerk}`);
    for (const t of c.lineage.traits)
      for (const p of t.startProducers ?? [])
        if (!ids.has(p)) bad(`trait ${t.id} names unknown producer ${p}`);
    for (const id of Object.keys(c.map.plots))
      if (!ids.has(id)) bad(`map plot for unknown producer ${id}`);
    if (c.map.trees.min > c.map.trees.max) bad('map trees min above max');
    // D344: a kind's ranges run low to high, and a kind and a trait never share an id.
    const named = new Set<string>();
    for (const k of [...c.map.kinds, ...c.map.traits]) {
      if (named.has(k.id)) bad(`map kind or trait ${k.id} is listed twice`);
      named.add(k.id);
    }
    for (const k of c.map.kinds) {
      if (k.width[0] > k.width[1]) bad(`map kind ${k.id}: width low above high`);
      if (k.trees.min > k.trees.max) bad(`map kind ${k.id}: trees min above max`);
      // D348: a theme with no water outside the fence draws no river's side.
      if (c.map.terrain.water === 'edge' && k.riverSides.every((w) => w === 0))
        bad(`map kind ${k.id}: no side for the river`);
    }
    const lands = new Map(c.map.lands.map((l) => [l.id, l]));
    for (const l of c.map.lands)
      for (const [id, r] of Object.entries(l.plots)) {
        if (c.producers.find((p) => p.id === id)?.land !== l.id)
          bad(`land ${l.id} has a plot for ${id}, which is not on it`);
        if (r.x < 0 || r.y < 0 || r.x + r.w > l.w || r.y + r.h > l.h)
          bad(`land ${l.id}: plot ${id} lies outside it`);
      }
    for (const p of c.producers) {
      if (p.land === undefined) continue;
      if (!lands.has(p.land)) bad(`producer ${p.id} on unknown land ${p.land}`);
      else if (!lands.get(p.land)!.plots[p.id]) bad(`producer ${p.id} has no plot on ${p.land}`);
      if (!c.lineage.traits.some((t) => t.opensLand === p.land))
        bad(`no trait opens ${p.land} for ${p.id}`);
    }
    for (const t of c.lineage.traits)
      if (t.opensLand !== undefined && !lands.has(t.opensLand))
        bad(`trait ${t.id} opens unknown land ${t.opensLand}`);
    for (const b of c.collection.breeds)
      if (!ids.has(b.producer)) bad(`breed ${b.id} names unknown producer ${b.producer}`);
    const seen = new Set<string>();
    for (const d of c.decorations) {
      if (seen.has(d.id)) bad(`duplicate decoration ${d.id}`);
      const first = !c.decorations.some((e) => e.kind === d.kind && seen.has(e.id));
      if (first && (d.cost !== 0 || d.festivals !== 0 || d.event !== undefined))
        bad(`decoration ${d.id} is its kind's default and must be free and open`);
      if (d.event !== undefined && !c.events.some((e) => e.id === d.event))
        bad(`decoration ${d.id} names unknown event ${d.event}`);
      seen.add(d.id);
    }
    for (const d of c.decorations)
      if (!c.terms.decorationKinds[d.kind]) bad(`terms.decorationKinds has no word for ${d.kind}`);
    for (const id of Object.keys(c.daily.orders.goods))
      if (!ids.has(id)) bad(`order goods for unknown producer ${id}`);
    const prizes = new Set<string>();
    for (const e of c.events) {
      if (prizes.has(e.prize.id)) bad(`duplicate event prize ${e.prize.id}`);
      prizes.add(e.prize.id);
    }
    if (c.producers[0]?.unlockCost !== 0)
      ctx.addIssue({ code: 'custom', message: 'the first producer must be free' });
  });

export type IdleContent = z.infer<typeof IdleContentSchema>;
export type IdleProducerT = z.infer<typeof IdleProducer>;
export type IdleMilestoneT = z.infer<typeof IdleMilestone>;
export type IdleChainT = z.infer<typeof IdleChain>;
export type IdleLinkT = z.infer<typeof IdleLink>;
export type IdleEffectSpecT = z.infer<typeof IdleEffectSpec>;
export type IdleUpgradeT = z.infer<typeof IdleUpgrade>;
export type IdleGoalT = z.infer<typeof IdleGoal>;
export type IdlePerkT = z.infer<typeof IdlePerk>;
export type IdleAlmanacBranchT = z.infer<typeof IdleAlmanacBranch>;
export type IdleProducerGroupT = z.infer<typeof IdleProducerGroup>;
export type IdleHeritageBranchT = z.infer<typeof IdleHeritageBranch>;
export type IdleTraitT = z.infer<typeof IdleTrait>;
export type IdleBarnLevelT = z.infer<typeof IdleBarnLevel>;
export type IdleDecorationT = IdleContent['decorations'][number];
export type IdleDecorationKind = IdleDecorationT['kind'];
export type IdleTermsT = z.infer<typeof IdleTerms>;
export type IdleTerrainT = z.infer<typeof IdleTerrain>;
export type IdleBonusKindT = z.infer<typeof IdleBonusKind>;
export type IdleCollectionCategoryT = z.infer<typeof IdleCollectionCategory>;

/**
 * D348, D351: the themes, by id. Each is a theme file of names laid over the one shared
 * economy (`content/idle_economy.json`), which holds every number. Adding a theme is a file
 * and a line here; the sim, the server and the save shape stay shared.
 */
const THEME_FILES: Record<string, unknown> = {
  farm: farmTheme,
  compute: computeTheme,
  // D420: names-only themes on the same economy.
  motorsport: motorsportTheme,
  arena: arenaTheme,
};

/** D348: the theme a world made before themes has, and the one a caller that names none gets. */
export const DEFAULT_IDLE_THEME = 'farm';

/** D348: every theme's id, the farm theme first. */
export function idleThemeIds(): string[] {
  return Object.keys(THEME_FILES);
}

/** D348: whether `id` names a theme. */
export function isIdleTheme(id: unknown): id is string {
  return typeof id === 'string' && Object.hasOwn(THEME_FILES, id);
}

/** D351: the shared economy before a theme is laid over it, as the file holds it. */
export const rawIdleEconomy = economy;

/** D351: a theme's raw content before validation: its names laid over the shared economy. */
export function rawIdleTheme(themeId: string = DEFAULT_IDLE_THEME): unknown {
  if (!isIdleTheme(themeId)) throw new Error(`unknown idle theme ${themeId}`);
  return mergeTheme(economy, THEME_FILES[themeId], themeId);
}

/** The farm theme's raw idle content before validation. */
export const rawIdleContent = rawIdleTheme(DEFAULT_IDLE_THEME) as z.input<typeof IdleContentSchema>;

/** Parse and validate a theme's idle content. Throws with every issue listed. */
export function loadIdleContent(themeId: string = DEFAULT_IDLE_THEME): IdleContent {
  const raw = themeId === DEFAULT_IDLE_THEME ? rawIdleContent : rawIdleTheme(themeId);
  const result = IdleContentSchema.safeParse(raw);
  if (!result.success) {
    const lines = result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`);
    throw new Error(`idle content (${themeId}) failed validation:\n${lines.join('\n')}`);
  }
  if (result.data.theme.id !== themeId)
    throw new Error(`idle theme ${themeId} calls itself ${result.data.theme.id}`);
  return result.data;
}

const cachedIdle = new Map<string, IdleContent>();

/**
 * D348: a theme's validated idle content, loaded once per process. With no theme, the farm theme's,
 * so a caller that knows of one theme only still works. Throws on an unknown theme.
 */
export function getIdleContent(themeId: string = DEFAULT_IDLE_THEME): IdleContent {
  let c = cachedIdle.get(themeId);
  if (!c) {
    c = loadIdleContent(themeId);
    cachedIdle.set(themeId, c);
  }
  return c;
}

/** D348: each theme's id and name, for a picker. */
export function idleThemes(): Array<{ id: string; name: string }> {
  return idleThemeIds().map((id) => ({ ...getIdleContent(id).theme }));
}
